<template>
    <div v-if="grids && grids.length > 0" class="datatable-grids">
        <Tabs :tabs="tabsData" />
    </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import type { Grid, Filter } from './types';
import { cloneFilters, findMatchingGrid } from './gridMatching';

interface Props {
    grids?: Grid[];
    currentFilters?: Filter[];
    currentSearch?: string | null;
}

const props = withDefaults(defineProps<Props>(), {
    grids: () => [],
    currentFilters: () => [],
    currentSearch: null,
});

const emit = defineEmits<{
    (event: 'gridSelected', filters: Filter[]): void;
}>();

const activeGridIndex = ref<number | null>(null);

/**
 * Convert grids to tabs format for Tabs component
 */
const tabsData = computed(() => {
    return props.grids.map((grid, index) => ({
        name: grid.name,
        current: activeGridIndex.value === index,
        click: () => selectGrid(index),
    }));
});

/**
 * Select a grid and emit its filters
 */
const selectGrid = (index: number) => {
    activeGridIndex.value = index;
    const selectedGrid = props.grids[index];
    if (selectedGrid) {
        // Clone so filter rows edited via v-model never mutate the grid definition.
        emit('gridSelected', cloneFilters(selectedGrid.filters));
    }
};

/**
 * Update active grid when current filters change
 * Logika: tab je aktivní, pokud jsou všechny jeho filtry podmnožinou aktuálních filtrů
 * (nejspecifičtější shoda vyhrává); tab bez filtrů jen při prázdných filtrech.
 */
watch(
    () => props.currentFilters,
    (newFilters) => {
        activeGridIndex.value = findMatchingGrid(props.grids, newFilters);
    },
    { deep: true, immediate: true },
);

// Také sledujme změny search samostatně
watch(
    () => props.currentSearch,
    () => {
        // Search changes don't affect grid matching logic directly,
        // but we keep this watcher for potential future use
    },
    { immediate: true },
);
</script>
